import React, { useEffect, useState } from 'react';
import { UserService } from '../../services/user.service';
import { InstitutionService } from '../../services/institution.service';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Modal, Button, Box, Select, MenuItem, FormControl, InputLabel, Tooltip, IconButton, Stack, Pagination, Alert, CircularProgress, TextField, Autocomplete, createFilterOptions } from '@mui/material';
import { ManageAccounts, Delete, LockReset, ToggleOn, ToggleOff, SupervisorAccount } from '@mui/icons-material';
import { UserType } from '../../interfaces/userType.enum';
import { ImpersonationGrantService } from '../../services/impersonation-grant.service';
import { IAvailableGrant } from '../../interfaces/impersonation-grant.interface';
import { IInstitution } from '../../interfaces/institution.interface';
import { displayUsername } from '../../utils/userUtils';

interface Institution {
    id: string;
    name: string;
}

interface User {
    id: string;
    username: string;
    phone: string;
    type: UserType;
    isActive: boolean;
    institutionName?: string; // Added to show which institution the user belongs to
}

const institutionFilterOptions = createFilterOptions<IInstitution>({ limit: 10 });

const AllInstitutionUsers = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [selectedType, setSelectedType] = useState<UserType | ''>('');
    const [userInstitutions, setUserInstitutions] = useState<Institution[]>([]);
    const [selectedInstitutionId, setSelectedInstitutionId] = useState<string>('');
    const [loadingInstitutions, setLoadingInstitutions] = useState(false);
    const [availableGrants, setAvailableGrants] = useState<IAvailableGrant[]>([]);
    const [institutions, setInstitutions] = useState<IInstitution[]>([]);
    const [filterInstitutionId, setFilterInstitutionId] = useState<string>('');
    const navigate = useNavigate();
    const { isAuthenticated, impersonate, user: authUser } = useAuth();
    const isSystemAdmin = authUser?.roles === 'SystemAdmin';
    const toast = useToast();
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0
    });
    const [page,] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        fetchAvailableGrants();
        fetchInstitutions();
    }, [isAuthenticated, navigate, page]);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(handle);
    }, [searchTerm]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchAllUsers(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, filterInstitutionId, isAuthenticated]);

    const fetchAvailableGrants = async () => {
        try {
            const grants = await ImpersonationGrantService.getAvailableGrants();
            setAvailableGrants(grants);
        } catch {
            // Silently fail — not critical
        }
    };

    const handleImpersonate = async (targetUserId: string) => {
        try {
            await impersonate(targetUserId);
            navigate('/');
        } catch (err: any) {
            toast.showApiError(err, 'Failed to impersonate user');
        }
    };

    const fetchInstitutions = async () => {
        try {
            const response = await InstitutionService.getInstitutions(0, 100);
            setInstitutions(response.content || []);
        } catch (err: any) {
            console.error('Error fetching institutions:', err);
        }
    };

    const fetchAllUsers = async (page: number = 0, institutionId?: string) => {
        setLoading(true);
        try {
            const selectedInstitution = institutionId !== undefined ? institutionId : filterInstitutionId;
            const response = selectedInstitution
                ? await UserService.getInstitutionUsers(page, 10, selectedInstitution, debouncedSearch)
                : await UserService.getUsers(page, 10, debouncedSearch);
            setUsers(response.content || []);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (err: any) {
            toast.showApiError(err, 'Failed to load users');
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (user: User) => {
        try {
            await UserService.toggleUserActive(user.id);
            toast.showSuccess(`User ${user.isActive !== false ? 'deactivated' : 'activated'} successfully`);
            await fetchAllUsers(pageInfo.currentPage);
        } catch (error: any) {
            toast.showApiError(error, 'Failed to update user status');
        }
    };

    const handleOpenTypeModal = (user: User) => {
        setSelectedUser(user);
        setSelectedType(user.type);
        setIsTypeModalOpen(true);
    };

    const handleCloseTypeModal = () => {
        setIsTypeModalOpen(false);
        setSelectedUser(null);
        setSelectedType('');
    };

    const handleOpenDeleteModal = (user: User) => {
        setSelectedUser(user);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setSelectedUser(null);
    };

    const handleOpenResetPasswordModal = async (user: User) => {
        setSelectedUser(user);
        setIsResetPasswordModalOpen(true);
        setLoadingInstitutions(true);
        setSelectedInstitutionId('');
        try {
            const institutions = await InstitutionService.getInstitutionsByUserId(user.id);
            setUserInstitutions(institutions);
        } catch (error) {
            console.error('Error fetching user institutions:', error);
            setUserInstitutions([]);
        } finally {
            setLoadingInstitutions(false);
        }
    };

    const handleCloseResetPasswordModal = () => {
        setIsResetPasswordModalOpen(false);
        setSelectedUser(null);
        setUserInstitutions([]);
        setSelectedInstitutionId('');
    };

    const handleTypeChange = async () => {
        if (!selectedUser || !selectedType) return;

        try {
            await UserService.updateUserType(selectedUser.id, selectedType);
            toast.showSuccess('User type updated successfully');
            await fetchAllUsers();
            handleCloseTypeModal();
        } catch (error: any) {
            console.error('Error updating user type:', error);
            toast.showApiError(error, 'Failed to update user type');
            handleCloseTypeModal();
        }
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) return;

        try {
            await UserService.deleteUser(selectedUser.id);
            toast.showSuccess('User deleted successfully');
            await fetchAllUsers();
            handleCloseDeleteModal();
        } catch (error: any) {
            console.error('Error deleting user:', error);
            toast.showApiError(error, 'Failed to delete user');
            handleCloseDeleteModal();
        }
    };

    const handleResetPassword = async () => {
        if (!selectedUser || !selectedInstitutionId) return;

        try {
            await UserService.resetUserPassword(selectedUser.id, selectedInstitutionId);
            toast.showSuccess('Password has been reset and sent to admin email. Please change password after login.');
            handleCloseResetPasswordModal();
        } catch (error) {
            console.error('Error resetting password:', error);
            toast.showApiError(error, 'Failed to reset password');
        }
    };

    const handleInstitutionFilterChange = (institutionId: string) => {
        setFilterInstitutionId(institutionId);
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchAllUsers(page - 1); // API uses 0-based indexing
    };

    if (loading) return <div data-testid="loading-state">Loading...</div>;

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>All Institution Users</h2>
                <Stack direction="row" spacing={2}>
                    <TextField
                        label="Search by username"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ width: 260 }}
                    />
                    <Autocomplete
                        sx={{ minWidth: 250 }}
                        size="small"
                        options={institutions}
                        filterOptions={institutionFilterOptions}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={institutions.find((institution) => institution.id === filterInstitutionId) ?? null}
                        onChange={(_event, newValue) => handleInstitutionFilterChange(newValue?.id ?? '')}
                        renderInput={(params) => (
                            <TextField {...params} label="Filter by Institution" placeholder="All Institutions" />
                        )}
                    />
                </Stack>
            </div>

            <div style={{ marginBottom: '20px' }}>
                Total Users: {pageInfo.totalElements}
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>Username</th>
                        <th style={tableHeaderStyle}>Phone</th>
                        <th style={tableHeaderStyle}>Type</th>
                        <th style={tableHeaderStyle}>Status</th>
                        <th style={tableHeaderStyle}>Institution</th>
                        <th style={tableHeaderStyle}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map(user => (
                        <tr key={user.username}>
                            <td style={tableCellStyle}>{displayUsername(user.username)}</td>
                            <td style={tableCellStyle}>{user.phone}</td>
                            <td style={tableCellStyle}>{user.type}</td>
                            <td style={tableCellStyle}>
                                <span style={{ color: user.isActive !== false ? 'green' : 'red' }}>
                                    {user.isActive !== false ? 'Active' : 'Inactive'}
                                </span>
                            </td>
                            <td style={tableCellStyle}>{user.institutionName || '-'}</td>
                            <td style={tableCellStyle}>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={user.isActive !== false ? "Deactivate User" : "Activate User"} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleToggleActive(user)}
                                            color={user.isActive !== false ? "success" : "default"}
                                            aria-label={user.isActive !== false ? "Deactivate User" : "Activate User"}
                                        >
                                            {user.isActive !== false ? <ToggleOn /> : <ToggleOff />}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Change Type" arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenTypeModal(user)}
                                            color="primary"
                                            aria-label="Change Type"
                                        >
                                            <ManageAccounts />
                                        </IconButton>
                                    </Tooltip>
                                    {isSystemAdmin && (
                                        <Tooltip title="Delete User" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDeleteModal(user)}
                                                color="error"
                                                aria-label="Delete User"
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Reset Password" arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenResetPasswordModal(user)}
                                            color="secondary"
                                            aria-label="Reset Password"
                                        >
                                            <LockReset />
                                        </IconButton>
                                    </Tooltip>
                                    {availableGrants.some(g => g.userId === user.id) && (
                                        <Tooltip title="Impersonate User" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleImpersonate(user.id)}
                                                color="warning"
                                                aria-label="Impersonate User"
                                            >
                                                <SupervisorAccount />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {pageInfo.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={pageInfo.totalPages}
                        page={pageInfo.currentPage + 1}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {/* Type Change Modal */}
            <Modal
                open={isTypeModalOpen}
                onClose={handleCloseTypeModal}
                aria-describedby="change-type-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h3>Change User Type</h3>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>User Type</InputLabel>
                        <Select
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value as UserType)}
                            label="User Type"
                        >
                            {Object.values(UserType).map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseTypeModal} variant="outlined">
                            Cancel
                        </Button>
                        <Button onClick={handleTypeChange} variant="contained" color="primary">
                            Save Changes
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                open={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                aria-describedby="delete-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h3>Delete User</h3>
                    <p>Are you sure you want to delete user "{displayUsername(selectedUser?.username)}"?</p>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseDeleteModal} variant="outlined">
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteUser} variant="contained" color="error">
                            Delete
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Reset Password Modal */}
            <Modal
                open={isResetPasswordModalOpen}
                onClose={handleCloseResetPasswordModal}
                aria-describedby="reset-password-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h3>Reset Password</h3>
                    <p>Are you sure you want to reset the password for user "{displayUsername(selectedUser?.username)}"?</p>

                    {loadingInstitutions ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : userInstitutions.length > 0 ? (
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel>Select Institution</InputLabel>
                            <Select
                                value={selectedInstitutionId}
                                onChange={(e) => setSelectedInstitutionId(e.target.value)}
                                label="Select Institution"
                            >
                                {userInstitutions.map((institution) => (
                                    <MenuItem key={institution.id} value={institution.id}>
                                        {institution.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            This user is not associated with any institution.
                        </Alert>
                    )}

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseResetPasswordModal} variant="outlined">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleResetPassword}
                            variant="contained"
                            color="primary"
                            disabled={!selectedInstitutionId || loadingInstitutions}
                        >
                            Reset Password
                        </Button>
                    </Box>
                </Box>
            </Modal>

        </div>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

export default AllInstitutionUsers;
